import { useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n/use-t';
import { CheckIcon, ChevronDownIcon, CloseIcon, SearchIcon } from '@/components/icons';
import { Button, Input, cx } from '@/components/ui';

export interface AsyncOption {
  id: string;
  label: string;
  /** A second line — a parent category, a product count, a region. */
  hint?: string;
  /** A picture the chosen value carries along — a product's, for the line it becomes. */
  image?: string | null;
}

export interface OptionPage {
  options: AsyncOption[];
  nextCursor: string | null;
}

/**
 * A single-value dropdown backed by an API rather than by a list held in memory.
 *
 * Every API-backed dropdown in this admin searches server-side with debounced
 * input and loads more on demand — the rule the platform inherited and that
 * these selects were the last to break. A `<select>` full of every category is
 * fine with forty of them and useless with four thousand, and the store that
 * has four thousand is exactly the one nobody tests against.
 *
 * Three details that make it usable rather than merely correct:
 *
 *  - **The chosen value keeps its name.** A value is rarely in the first page of
 *    matches, so `fetchOne` resolves the label the picker was not told — a form
 *    showing a UUID has lost the answer it is meant to display.
 *  - **Typing cancels the previous request.** A fast typist would otherwise race
 *    their own results and see the answer to a prefix they have moved past.
 *  - **It closes on Escape and on a click outside**, because a panel that traps
 *    the page is worse than no panel.
 */
export function AsyncSelect({
  value,
  onChange,
  fetchPage,
  fetchOne,
  placeholder,
  emptyLabel,
  disabled,
  allowClear = true,
  id,
}: {
  value: string;
  /** The option is passed back too — the caller usually needs more than the id. */
  onChange: (id: string, option: AsyncOption | null) => void;
  fetchPage: (search: string, cursor?: string) => Promise<OptionPage>;
  /** Names a value the current search does not include. */
  fetchOne?: (id: string) => Promise<AsyncOption | null>;
  placeholder?: string;
  /** What "nothing chosen" reads as. */
  emptyLabel?: string;
  disabled?: boolean;
  allowClear?: boolean;
  id?: string;
}) {
  const t = useT();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<AsyncOption[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState<AsyncOption | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const container = useRef<HTMLDivElement>(null);

  // Debounced search; the cleanup cancels the keystroke's request when the next
  // one arrives, so results always answer the latest term.
  useEffect(() => {
    if (!open) return;

    if (timer.current) clearTimeout(timer.current);
    let cancelled = false;

    timer.current = setTimeout(() => {
      setLoading(true);
      void fetchPage(search)
        .then((page) => {
          if (cancelled) return;
          setOptions(page.options);
          setNextCursor(page.nextCursor);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
    // Deliberately not depending on `fetchPage`: callers build it inline, so a
    // dependency on it would refetch on every render, forever.
  }, [search, open]);

  // The label of whatever is already selected.
  useEffect(() => {
    if (!value) {
      setChosen(null);
      return;
    }
    if (chosen?.id === value) return;

    const known = options.find((option) => option.id === value);
    if (known) {
      setChosen(known);
      return;
    }

    if (!fetchOne) return;

    let cancelled = false;
    void fetchOne(value).then((option) => {
      if (!cancelled && option) setChosen(option);
    });

    return () => {
      cancelled = true;
    };
    // Only the value matters here; `chosen` and `options` are read to avoid a
    // fetch, and listing them would re-run this on every keystroke.
  }, [value]);

  // Escape and outside clicks close it; a panel that traps the page is worse
  // than no panel.
  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const loadMore = async () => {
    if (!nextCursor || loading) return;

    setLoading(true);
    try {
      const page = await fetchPage(search, nextCursor);
      setOptions((current) => [...current, ...page.options]);
      setNextCursor(page.nextCursor);
    } finally {
      setLoading(false);
    }
  };

  const select = (option: AsyncOption | null) => {
    setChosen(option);
    onChange(option?.id ?? '', option);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={container} className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cx(
          'flex min-h-10 w-full items-center justify-between gap-2 rounded-control border bg-surface px-3 text-start text-sm shadow-[0_1px_1px_rgb(0_0_0/0.02)] transition-[border-color,box-shadow]',
          open ? 'border-brand-500 ring-3 ring-brand-500/20' : 'border-line-strong',
          disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-ink-faint/60',
          chosen ? 'text-ink' : 'text-ink-muted',
        )}
      >
        <span className="truncate">{chosen?.label ?? emptyLabel ?? t('anyValue')}</span>
        <ChevronDownIcon className={cx('size-4 shrink-0 text-ink-faint transition-transform', open && 'rotate-180')} />
      </button>

      {open ? (
        <div className="animate-pop absolute z-40 mt-1.5 w-full min-w-60 rounded-panel border border-line bg-surface p-1.5 shadow-float">
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
              <SearchIcon className="size-4" />
            </span>
            <Input
              value={search}
              placeholder={placeholder ?? t('searchLabel')}
              onChange={(event) => setSearch(event.target.value)}
              className="ps-9"
              autoFocus
            />
          </div>

          <ul role="listbox" className="mt-1.5 flex max-h-64 flex-col gap-0.5 overflow-y-auto">
            {allowClear ? (
              <li>
                <button
                  type="button"
                  onClick={() => select(null)}
                  className="w-full rounded-lg px-3 py-2 text-start text-sm text-ink-muted hover:bg-ink/[0.05]"
                >
                  {emptyLabel ?? t('anyValue')}
                </button>
              </li>
            ) : null}

            {options.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.id === value}
                  onClick={() => select(option)}
                  className={cx(
                    'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start text-sm',
                    option.id === value ? 'bg-brand-50 font-medium text-brand-700' : 'text-ink hover:bg-ink/[0.05]',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{option.label}</span>
                    {option.hint ? (
                      <span className="block truncate text-xs font-normal text-ink-muted">{option.hint}</span>
                    ) : null}
                  </span>
                  {option.id === value ? <CheckIcon className="size-4 shrink-0" /> : null}
                </button>
              </li>
            ))}

            {!loading && options.length === 0 ? (
              <li className="px-3 py-2 text-sm text-ink-muted">{t('noResults')}</li>
            ) : null}
          </ul>

          {loading ? <p className="px-3 py-2 text-xs text-ink-muted">{t('loading')}</p> : null}

          {nextCursor ? (
            <div className="pt-2">
              <Button
                variant="secondary"
                disabled={loading}
                onClick={() => void loadMore()}
                className="w-full"
              >
                {t('loadMore')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The same picker for several values.
 *
 * Chosen entries are chips, and a chip resolves its own label — a campaign
 * targeting six categories must name all six, including the ones no current
 * search happens to return, or it looks like it lost one.
 */
export function AsyncMultiSelect({
  values,
  onChange,
  fetchPage,
  fetchOne,
  placeholder,
  addLabel,
  id,
}: {
  values: string[];
  onChange: (ids: string[]) => void;
  fetchPage: (search: string, cursor?: string) => Promise<OptionPage>;
  fetchOne?: (id: string) => Promise<AsyncOption | null>;
  placeholder?: string;
  addLabel?: string;
  id?: string;
}) {
  const t = useT();
  const [labels, setLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!fetchOne) return;

    let cancelled = false;
    const missing = values.filter((value) => !labels[value]);

    void Promise.all(missing.map((value) => fetchOne(value))).then((resolved) => {
      if (cancelled) return;

      const next: Record<string, string> = {};
      for (const option of resolved) {
        if (option) next[option.id] = option.label;
      }
      if (Object.keys(next).length > 0) setLabels((current) => ({ ...current, ...next }));
    });

    return () => {
      cancelled = true;
    };
    // `labels` is written here, so depending on it would loop.
  }, [values]);

  return (
    <div className="flex flex-col gap-2">
      <AsyncSelect
        id={id}
        value=""
        allowClear={false}
        emptyLabel={addLabel ?? t('addItem')}
        placeholder={placeholder}
        fetchPage={fetchPage}
        onChange={(chosenId, option) => {
          if (!chosenId || values.includes(chosenId)) return;
          if (option) setLabels((current) => ({ ...current, [option.id]: option.label }));
          onChange([...values, chosenId]);
        }}
      />

      {values.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {values.map((value) => (
            <span
              key={value}
              className="inline-flex h-7 items-center gap-1 rounded-full bg-ink/[0.05] ps-3 pe-1 text-xs font-medium text-ink ring-1 ring-ink/10 ring-inset"
            >
              {labels[value] ?? value}
              <button
                type="button"
                aria-label={`${t('remove')} — ${labels[value] ?? value}`}
                onClick={() => onChange(values.filter((entry) => entry !== value))}
                className="inline-flex size-5 items-center justify-center rounded-full text-ink-muted transition hover:bg-danger/10 hover:text-danger"
              >
                <CloseIcon className="size-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
